/**
 * One row of the Supplier Performance table — a supplier as a whole, or (the
 * `site` variant) that supplier at one customer site.
 *
 * Split out of PerformanceTable (which was past the 500-line limit). The
 * frozen-column `left` offsets come from `frozenColumns` in
 * PerformanceTableHeader.
 *
 * Customer review 11 Sep (item 3/4): the top-level row is the supplier over
 * every customer site in view. Its Customer cell says so ("All sites (N)", the
 * list and each site's supplier code on hover), and a chevron opens one row
 * per site beneath it — the old portal's group → supplier → site model
 * (the reviewer's m04b). A site row's count and money cells open the same
 * drill-downs as the supplier row, scoped to that one site (the row passes
 * its `site`; useDrilldownModal lists that site only), so each modal lists
 * exactly the records behind the figure clicked — Customer review 11 Sep
 * (stream I, from B).
 */
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { SupplierProfile, SupplierRatingThreshold, SupplierCustomerSite } from '../../lib/api';
import {
  getGradeInfo, getRatingStyle, getOTDColor, formatCurrency, isNARating, customerScopeLabel,
} from './utils';
import { selfRowOf } from './selfPerformance';
import { frozenColumns } from './PerformanceTableHeader';
import { DrillSite } from './types';

// `site` (last): set by a per-site breakdown row — the drill lists that site only.
export interface RowCallbacks {
  // CR-6134: Added otdStatus parameter for Ontime/Late/Early GRN drilldowns
  onOpenGRNModal: (
    supplierId: number,
    supplierName: string,
    filterType?: string,
    value?: number,
    otdStatus?: 'on_time' | 'late' | 'early',
    site?: DrillSite,
  ) => void;
  onOpenNCRModal: (supplierId: number, supplierName: string, filterType?: string, value?: number, site?: DrillSite) => void;
  onOpenPOLineModal: (
    supplierId: number,
    supplierName: string,
    filterType?: string,
    value?: number,
    additionalFilters?: { status?: string; on_hold?: boolean },
    site?: DrillSite,
  ) => void;
}

interface PerformanceTableRowProps extends RowCallbacks {
  supplier: SupplierProfile;
  /** Grade bands for THIS row (the row's customer's on the supplier page). */
  thresholds: SupplierRatingThreshold[];
  currency: string;
  viewerIsSupplier: boolean;
  showSupplierSite: boolean;
  /** `site`: the supplier at one customer site, under its supplier row. */
  variant?: 'supplier' | 'site';
  /** The site a `site` row stands for. */
  site?: SupplierCustomerSite;
  /** The row covers several sites and can open its per-site breakdown. */
  expandable?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
}

export function PerformanceTableRow({
  supplier,
  thresholds,
  currency,
  viewerIsSupplier,
  showSupplierSite,
  variant = 'supplier',
  site,
  expandable = false,
  expanded = false,
  onToggleExpand,
  onOpenGRNModal,
  onOpenNCRModal,
  onOpenPOLineModal,
}: PerformanceTableRowProps) {
  const self = selfRowOf(supplier);
  const isSiteRow = variant === 'site';
  const rowId = self?.self_key ?? supplier.company;
  const rowName = self?.customer_name ?? supplier.company_name;
  const { customerLeft, ratingLeft } = frozenColumns(viewerIsSupplier, showSupplierSite);
  const scope = customerScopeLabel(supplier.customer_sites);
  const singleSite = supplier.customer_sites?.length === 1 ? supplier.customer_sites[0] : undefined;
  // Solid, not a translucent tint: the frozen cells sit over the scrolled
  // columns, and a see-through background let those numbers bleed through.
  const rowBg = isSiteRow ? 'bg-slate-50 dark:bg-slate-900' : 'bg-background';
  const money = (value: string | number | null | undefined) =>
    formatCurrency(value == null ? null : String(value), supplier.display_currency || currency);
  // The drill scope a site row adds (undefined on a supplier row).
  const drillSite: DrillSite | undefined = isSiteRow && site ? { id: site.id, name: site.name } : undefined;
  const at = drillSite ? ` at ${drillSite.name}` : '';

  // A drillable count — on a site row it opens that site's records only.
  const count = (
    value: number | undefined,
    className: string,
    title: string,
    open: () => void,
  ) => (
    <button onClick={open} className={`text-sm ${className} hover:underline cursor-pointer`} title={`${title}${at}`}>
      {(value || 0).toLocaleString()}
    </button>
  );

  const co2Count = supplier.carbon_emission_asn_count ?? 0;

  return (
    <tr
      className={`border-b transition-colors ${isSiteRow ? 'bg-slate-50 dark:bg-slate-900' : 'hover:bg-muted/30'}`}
      data-testid={isSiteRow ? `site-row-${supplier.company}-${site?.id}` : `supplier-row-${supplier.company}`}
    >
      {!viewerIsSupplier && (
        // Supplier Name - Frozen (with the breakdown chevron)
        <td className={`p-3 sticky left-0 ${rowBg} z-10`}>
          {isSiteRow ? (
            <div className="pl-7">
              <div className="text-sm text-muted-foreground">{supplier.company_name}</div>
              {site?.supplier_code && (
                <div className="text-xs text-muted-foreground">Code {site.supplier_code}</div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {expandable ? (
                <button
                  type="button"
                  onClick={onToggleExpand}
                  aria-expanded={expanded}
                  aria-label={expanded ? `Hide ${supplier.company_name} by customer site` : `Show ${supplier.company_name} by customer site`}
                  title={expanded ? 'Hide the per-site breakdown' : 'Show performance at each customer site'}
                  className="h-5 w-5 shrink-0 inline-flex items-center justify-center rounded hover:bg-muted"
                >
                  {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
              ) : (
                <span className="w-5 shrink-0" />
              )}
              {supplier.is_preferred && (
                <span className="text-yellow-500" title="Strategic Supplier">
                  ★
                </span>
              )}
              <div className="font-medium text-sm">{supplier.company_name}</div>
            </div>
          )}
        </td>
      )}

      {/* Q8: at GROUP view a supplier's rows span sibling sites — show which. */}
      {showSupplierSite && (
        <td className={`p-3 sticky left-0 ${rowBg} z-10`}>
          <div className="font-medium text-sm">{self?.supplier_site_name}</div>
          {self?.is_own_site === false && (
            <div className="text-xs text-muted-foreground">sister site</div>
          )}
        </td>
      )}
      {/* Customer - Frozen. SI-16: on the supplier page this is the row's
          customer (name, group beneath). */}
      <td className={`p-3 sticky ${rowBg} z-10`} style={{ left: `${customerLeft}px` }}>
        {viewerIsSupplier ? (
          <div>
            <div className="font-medium text-sm">{rowName}</div>
            {self?.customer_group && (
              <div className="text-xs text-muted-foreground">{self.customer_group}</div>
            )}
          </div>
        ) : isSiteRow ? (
          <span className="text-xs font-medium">{site?.name}</span>
        ) : (
          <div className="text-center" title={scope.title}>
            <span className="text-xs">{scope.label}</span>
            {singleSite?.supplier_code && (
              <div className="text-[11px] text-muted-foreground">Code {singleSite.supplier_code}</div>
            )}
          </div>
        )}
      </td>

      {/* Rating - Frozen */}
      <td className={`p-3 text-center sticky ${rowBg} z-10 border-r`} style={{ left: `${ratingLeft}px` }}>
        {isNARating(supplier) ? (
          <Badge variant="secondary" className="font-bold">
            N/A
          </Badge>
        ) : (
          <Badge
            className="font-bold"
            style={getRatingStyle(supplier.combined_score ?? undefined, thresholds)}
            title={`Score ${supplier.combined_score?.toFixed(1)}`}
          >
            {getGradeInfo(supplier.combined_score ?? undefined, thresholds).label}
          </Badge>
        )}
      </td>

      {/* Warehouse */}
      <td className="p-3 text-center">
        <span className="text-xs">{supplier.warehouse_name || '-'}</span>
      </td>

      {/* Quality Metrics */}
      <td className="p-3 text-center border-l">
        {count(supplier.qty_received, 'text-primary', 'View GRN details',
          () => onOpenGRNModal(rowId, rowName, 'Qty Received', supplier.qty_received, undefined, drillSite))}
      </td>
      <td className="p-3 text-center">
        {count(supplier.reject_qty, 'text-red-600', 'View NCR details',
          () => onOpenNCRModal(rowId, rowName, 'Qty Rejected', supplier.reject_qty, drillSite))}
      </td>
      <td className="p-3 text-center">
        <span className="text-sm">{supplier.customer_liability_ppm?.toFixed(2) || 0}</span>
      </td>
      <td className="p-3 text-center">
        <span className="text-sm">{supplier.supplier_liability_ppm?.toFixed(2) || 0}</span>
      </td>
      <td className="p-3 text-center">
        <span className="text-sm font-medium">{supplier.total_ppm?.toFixed(2) || 0}</span>
      </td>
      <td className="p-3 text-center">
        {count(supplier.ncr_count, 'text-primary', 'View NCR details',
          () => onOpenNCRModal(rowId, rowName, 'NCRs', supplier.ncr_count, drillSite))}
      </td>
      <td className="p-3 text-center">
        {(supplier.return_count ?? 0) > 0 ? (
          count(supplier.return_count, 'text-orange-600', 'View Return to Supplier NCRs',
            () => onOpenNCRModal(rowId, rowName, 'Returns', supplier.return_count, drillSite))
        ) : (
          <span className="text-sm">0</span>
        )}
      </td>
      {!viewerIsSupplier && (
        <td className="p-3 text-center">
          <span className="text-sm">{supplier.response_score || 0}</span>
        </td>
      )}

      {/* Cost Metrics */}
      <td className="p-3 text-center border-l">
        <button
          onClick={() => onOpenGRNModal(rowId, rowName, 'Total Value', parseFloat(supplier.total_value || '0'), undefined, drillSite)}
          className="text-sm font-medium text-primary hover:underline cursor-pointer"
          title={`View GRN details${at}`}
        >
          {money(supplier.total_value)}
        </button>
      </td>
      <td className="p-3 text-center">
        <button
          onClick={() => onOpenNCRModal(rowId, rowName, 'Reject Value', parseFloat(supplier.reject_value || '0'), drillSite)}
          className="text-sm text-red-600 hover:underline cursor-pointer"
          title={`View NCR details${at}`}
        >
          {money(supplier.reject_value)}
        </button>
      </td>
      {/* Cost of quality (Customer review 11 Sep, msg 10): calculated figures, no click-through. */}
      <td className="p-3 text-center">
        <span className="text-sm">{money(supplier.customer_cost_of_quality)}</span>
      </td>
      <td className="p-3 text-center">
        <span className="text-sm">{money(supplier.supplier_cost_of_quality)}</span>
      </td>
      <td className="p-3 text-center">
        <span className="text-sm font-medium">{money(supplier.total_cost_of_quality)}</span>
      </td>

      {/* Delivery Metrics (PO Line) */}
      <td className="p-3 text-center border-l">
        {count(supplier.po_line_count, 'text-primary', 'View PO Line details',
          () => onOpenPOLineModal(rowId, rowName, 'PO Lines', supplier.po_line_count, undefined, drillSite))}
      </td>
      <td className="p-3 text-center">
        {count(supplier.otif_count, 'text-green-600', 'View OTIF details',
          () => onOpenPOLineModal(rowId, rowName, 'OTIF', supplier.otif_count, undefined, drillSite))}
      </td>
      <td className="p-3 text-center">
        {count(supplier.arrears_count, 'text-red-600', 'View Arrears details',
          () => onOpenPOLineModal(rowId, rowName, 'Arrears', supplier.arrears_count, undefined, drillSite))}
      </td>
      <td className="p-3 text-center">
        <span
          className={`text-sm font-semibold ${supplier.otif_score_percentage == null ? 'text-muted-foreground' : getOTDColor(supplier.otif_score_percentage)}`}
        >
          {supplier.otif_score_percentage == null ? 'N/A' : `${supplier.otif_score_percentage.toFixed(1)}%`}
        </span>
      </td>
      <td className="p-3 text-center">
        {count(supplier.qty_diff, 'text-primary', 'View Qty Diff details',
          () => onOpenPOLineModal(rowId, rowName, 'Qty Diff', supplier.qty_diff, undefined, drillSite))}
      </td>

      {/* Delivery Metrics (GRN) */}
      <td className="p-3 text-center border-l">
        {count(supplier.grn_count, 'text-primary', 'View GRN details',
          () => onOpenGRNModal(rowId, rowName, 'No. of GRNs', supplier.grn_count, undefined, drillSite))}
      </td>
      {/* CR-6134: Ontime/Late/Early now open GRN modal filtered by OTD status */}
      <td className="p-3 text-center">
        {count(supplier.ontime_count, 'text-green-600', 'View Ontime deliveries (GRN)',
          () => onOpenGRNModal(rowId, rowName, 'Ontime Deliveries', supplier.ontime_count, 'on_time', drillSite))}
      </td>
      <td className="p-3 text-center">
        {count(supplier.late_count, 'text-red-600', 'View Late deliveries (GRN)',
          () => onOpenGRNModal(rowId, rowName, 'Late Deliveries', supplier.late_count, 'late', drillSite))}
      </td>
      <td className="p-3 text-center">
        {count(supplier.early_count, 'text-blue-600', 'View Early deliveries (GRN)',
          () => onOpenGRNModal(rowId, rowName, 'Early Deliveries', supplier.early_count, 'early', drillSite))}
      </td>

      {/* CO2 Emissions — Customer review 11 Sep (msg 16): Σ of the CO2 captured
          on the row's ASNs; "—" when none carried a figure, so an absence of
          data never reads as a measured zero. */}
      <td className="p-3 text-center border-l">
        {co2Count === 0 ? (
          <span className="text-sm text-muted-foreground" title="No CO2 captured on this supplier's ASNs in this period">
            —
          </span>
        ) : (
          <span className="text-sm" title={`From ${co2Count.toLocaleString()} ASN${co2Count === 1 ? '' : 's'} with CO2 captured`}>
            {(supplier.carbon_emission || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} kg
          </span>
        )}
      </td>
    </tr>
  );
}
